import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

const AccountPage = () => {
  const { user, updateUser } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  const hasChanges = name.trim() !== (user?.name || '');

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await updateUser({ name: name.trim() });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch (err) {
      console.error('Error updating profile:', err);
      alert('Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  const initials = user?.name
    ? user.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
    : 'U';

  return (
    <div className="ayni-page" style={{ minHeight: 'calc(100vh - 40px)' }}>
      <div className="ayni-head">
        <h1 className="ayni-display-lg" style={{ margin: 0 }}>Account</h1>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: '280px 1fr', gap: '24px',
        maxWidth: '900px',
      }}>
        {/* Left card — Avatar */}
        <div style={{
          backgroundColor: 'var(--bg-card)', borderRadius: '12px',
          border: '1px solid var(--border-default)',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)', padding: '32px 24px',
          textAlign: 'center',
        }}>
          {user?.avatar_url ? (
            <img
              src={user.avatar_url}
              alt=""
              style={{
                width: 80, height: 80, borderRadius: '50%',
                objectFit: 'cover', margin: '0 auto 16px',
                display: 'block',
              }}
            />
          ) : (
            <div style={{
              width: 80, height: 80, borderRadius: '50%',
              backgroundColor: 'var(--color-primary)', color: 'white',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '28px', fontWeight: '600', margin: '0 auto 16px',
            }}>
              {initials}
            </div>
          )}
          <div style={{
            fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)',
            marginBottom: '4px',
          }}>
            {user?.name || 'User'}
          </div>
          <div style={{
            fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px',
          }}>
            {user?.email || ''}
          </div>
          <span style={{
            display: 'inline-block', padding: '3px 10px', fontSize: '11px',
            fontWeight: '500', borderRadius: '10px', textTransform: 'capitalize',
            backgroundColor: user?.role === 'superadmin' ? 'var(--badge-purple-bg)' : 'var(--badge-green-bg)',
            color: user?.role === 'superadmin' ? 'var(--badge-purple-text)' : 'var(--badge-green-text)',
          }}>
            {user?.role || 'user'}
          </span>
        </div>

        {/* Right card — Profile form */}
        <div style={{
          backgroundColor: 'var(--bg-card)', borderRadius: '12px',
          border: '1px solid var(--border-default)', padding: '24px',
        }}>
          <div style={{ marginBottom: '20px' }}>
            <h2 style={{
              fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)',
              margin: '0 0 4px 0',
            }}>
              Profile
            </h2>
            <p style={{
              fontSize: '13px', color: 'var(--text-muted)', margin: 0,
            }}>
              Update your account information
            </p>
          </div>

          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px',
            borderTop: '1px solid var(--border-light)', paddingTop: '20px',
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{
                fontSize: '12px', fontWeight: '500', color: 'var(--text-secondary)',
              }}>
                Name
              </label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={{
                  padding: '10px 12px', border: '1px solid var(--border-input)',
                  borderRadius: '8px', fontSize: '14px',
                  backgroundColor: 'var(--bg-input)', color: 'var(--text-primary)',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{
                fontSize: '12px', fontWeight: '500', color: 'var(--text-secondary)',
              }}>
                Email
              </label>
              <input
                value={user?.email || ''}
                readOnly
                style={{
                  padding: '10px 12px', border: '1px solid var(--border-input)',
                  borderRadius: '8px', fontSize: '14px',
                  backgroundColor: 'var(--bg-subtle)', color: 'var(--text-muted)',
                  boxSizing: 'border-box', cursor: 'not-allowed',
                }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{
                fontSize: '12px', fontWeight: '500', color: 'var(--text-secondary)',
              }}>
                Role
              </label>
              <input
                value={user?.role || 'user'}
                readOnly
                style={{
                  padding: '10px 12px', border: '1px solid var(--border-input)',
                  borderRadius: '8px', fontSize: '14px', textTransform: 'capitalize',
                  backgroundColor: 'var(--bg-subtle)', color: 'var(--text-muted)',
                  boxSizing: 'border-box', cursor: 'not-allowed',
                }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{
                fontSize: '12px', fontWeight: '500', color: 'var(--text-secondary)',
              }}>
                Member since
              </label>
              <input
                value={user?.created_at ? new Date(user.created_at).toLocaleDateString() : '--'}
                readOnly
                style={{
                  padding: '10px 12px', border: '1px solid var(--border-input)',
                  borderRadius: '8px', fontSize: '14px',
                  backgroundColor: 'var(--bg-subtle)', color: 'var(--text-muted)',
                  boxSizing: 'border-box', cursor: 'not-allowed',
                }}
              />
            </div>
          </div>

          <div style={{
            display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
            gap: '10px', marginTop: '24px', borderTop: '1px solid var(--border-light)',
            paddingTop: '16px',
          }}>
            {savedFlash && (
              <span style={{ fontSize: '13px', color: '#10b981', fontWeight: 500 }}>Saved!</span>
            )}
            <button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              style={{
                padding: '10px 24px', borderRadius: '8px', border: 'none',
                fontSize: '13px', fontWeight: '500',
                cursor: saving || !hasChanges ? 'not-allowed' : 'pointer',
                backgroundColor: saving || !hasChanges ? 'var(--bg-subtle)' : 'var(--color-primary)',
                color: saving || !hasChanges ? 'var(--text-muted)' : 'white',
                transition: 'background-color 0.15s',
              }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountPage;
